import { getParent, types } from 'mobx-state-tree';
import { differenceInCalendarDays, isBefore } from 'date-fns';

const PolicyEquityStatus = types
  .model({
    policyDays: types.maybe(types.number),
    blendedDailyRate: types.maybe(types.number),
    status: types.optional(types.enumeration(['loading', 'done', 'error']), 'loading'),
    premiumBilled: types.maybe(types.number),
    premiumReceived: types.maybe(types.number),
    equityDaysRemaining: types.maybe(types.number),
    paidToDate: types.maybeNull(types.Date),
    equityDays: types.maybe(types.number)
  })
  .views((self) => ({
    get policy() {
      return getParent(self);
    },

    get totalBilled() {
      const { surplusContribution, totalFeeCost } = self.policy;

      return self.premiumBilled + surplusContribution + totalFeeCost;
    },
    get premium() {
      const { premium } = self.policy;

      return premium;
    },
    get totalFeeCost() {
      const { totalFeeCost } = self.policy;
      return totalFeeCost;
    },
    get surplusContribution() {
      const { surplusContribution } = self.policy;

      return surplusContribution;
    },
    get totalPrice() {
      const { surplusContribution, totalFeeCost, premium } = self.policy;

      return premium + totalFeeCost + surplusContribution;
    },

    get totalReceived() {
      return self.policy.totalReceived;
    },

    get feesBilled() {
      return self.policy.totalFeeCost;
    },

    get writeOffTotal() {
      return self.policy.writeOffTotal;
    },

    get feesPaidInFull() {
      const { feesBilled, remainingAfterSurplus, surplusPaidInFull } = self;

      return surplusPaidInFull && remainingAfterSurplus >= feesBilled;
    },

    get premiumPaidInFull() {
      const { feesPaidInFull, premium, remainingAfterFees } = self;

      return feesPaidInFull && remainingAfterFees >= premium;
    },

    get surplusBilled() {
      return self.policy.surplusContribution;
    },

    get surplusPaidInFull() {
      const { surplusBilled, totalReceived } = self;

      return totalReceived >= surplusBilled;
    },

    get remainingAfterSurplus() {
      const { surplusContribution, surplusPaidInFull, totalReceived } = self;

      return surplusPaidInFull ? totalReceived - surplusContribution : 0;
    },

    get remainingAfterFees() {
      const { totalFeeCost, feesPaidInFull, remainingAfterSurplus } = self;

      return feesPaidInFull ? remainingAfterSurplus - totalFeeCost : 0;
    },

    get billedCostBreakdown() {
      const { feesBilled: fees, premiumBilled: premium, surplusBilled: surplus, totalBilled: total } = self;

      return {
        surplus,
        fees,
        premium,
        total
      };
    },

    get remainingCostBreakdown() {
      const {
        totalFeeCost,
        feesPaidInFull,
        premium,
        premiumPaidInFull,
        remainingAfterSurplus,
        remainingAfterFees,
        surplusContribution,
        surplusPaidInFull,
        totalReceived,
        totalPrice
      } = self;

      return {
        surplus: surplusPaidInFull ? 0 : surplusContribution - totalReceived,
        fees: feesPaidInFull ? 0 : totalFeeCost - remainingAfterSurplus,
        premium: premiumPaidInFull ? 0 : premium - remainingAfterFees,
        total: totalReceived >= totalPrice ? 0 : totalPrice - totalReceived
      };
    },

    get paidCostBreakdown() {
      const {
        feesBilled,
        feesPaidInFull,
        premiumBilled,
        premiumPaidInFull,
        remainingAfterFees,
        remainingAfterSurplus,
        surplusBilled,
        surplusPaidInFull,
        totalReceived,
        writeOffTotal
      } = self;

      return {
        surplus: surplusPaidInFull ? surplusBilled : totalReceived,
        fees: feesPaidInFull ? feesBilled : remainingAfterSurplus,
        premium: premiumPaidInFull ? premiumBilled + writeOffTotal : remainingAfterFees,
        total: totalReceived
      };
    },

    get calculatePolicyDays() {
      const { policy } = self.policy; //policy Store
      const { segments, endDate } = policy; //individual policy
      const historySegments = segments.filter((segment) => new Date(segment.startDate) <= new Date(policy && endDate));
      return historySegments.reduce((acc, segment) => acc + segment.segmentDays, 0);
    },

    get calculateBlendedDailyRate() {
      const { premium } = self.policy.policy; //policy Store
      const policyDays = self.calculatePolicyDays;
      return premium / policyDays;
    },

    // totalReceived is the amount that is coming from chargedesk, BQ is no longer used
    get calculateEquityDays() {
      const { totalReceived } = self;
      const { policy } = self.policy;

      const { effectiveDate, endDate, segments } = policy;

      let equityDays = 0;
      let remainingDaysBudget = totalReceived;
      let budgetExceeded = false;

      const historySegments = segments.filter((segment) => new Date(segment.startDate) <= new Date(policy && endDate));

      for (const { segmentDays, dailyRate } of historySegments) {
        if (segmentDays >= 0 && !budgetExceeded) {
          const segmentDailyRate = dailyRate;

          for (let i = 0; i < segmentDays; i++) {
            if (remainingDaysBudget >= segmentDailyRate) {
              equityDays++;
              remainingDaysBudget -= segmentDailyRate;
            } else {
              budgetExceeded = true;
            }
          }
        }
      }

      const today = new Date();
      const paidToDate = new Date(effectiveDate);
      paidToDate.setDate(paidToDate.getDate() + equityDays);

      const equityDaysRemaining = differenceInCalendarDays(paidToDate, today);

      const policyEndDate = new Date(endDate);
      const endDateInPast = isBefore(policyEndDate, today);

      return { equityDays, equityDaysRemaining, paidToDate: endDateInPast ? null : paidToDate };
    }
  }))
  .actions((self) => ({
    fetchPolicyEquityStatus: function fetchPolicyEquityStatus() {
      // Ensure policy store is loaded before attempting to calculate equity, prevents undefined errors
      if (!self.policy) {
        return;
      }

      const { equityDays, equityDaysRemaining, paidToDate } = self.calculateEquityDays;

      self.policyDays = self.calculatePolicyDays;
      self.blendedDailyRate = self.calculateBlendedDailyRate;
      self.equityDaysRemaining = equityDaysRemaining;
      self.paidToDate = paidToDate;
      self.equityDays = equityDays;
      self.premiumReceived = self.policy.totalReceived;
      self.premiumBilled = self.policy.policy.premium;
    }
  }));

export default PolicyEquityStatus;
